/* AnalysesGuide — patient-friendly tour за меню Анализи (4 sub-tab-а). */
import React from "react";
import {
  FlaskConical, Network, Dna, ScanLine, Activity, MessageSquareText,
  Calculator,
} from "lucide-react";
import MenuGuide from "@/components/guides/MenuGuide";

const MODULES = [
  {
    id: "lab",
    icon: FlaskConical, color: "#0EA5E9",
    name_bg: "Лаборатории — качване на кръвни изследвания",
    name_en: "Lab — upload blood tests",
    aiPowered: true,
    purpose_bg: "Качвате документ или снимка на кръвните си изследвания. Разчитаме резултатите, обясняваме ги на разбираем език и показваме кои стойности са в границите.",
    purpose_en: "Upload PDF/JPG of your blood tests. AI reads the results, explains them in plain language and shows which values are within range.",
    benefit_bg: "Спирате да гадаете какво значат стрелките нагоре и надолу. Получавате обяснение, съобразено с Вашия профил.",
    benefit_en: "You stop guessing what up/down arrows mean. You get personalized context for you.",
    action_bg: "Раздел „Лаборатории“ → „Качване на резултати“ → пуснете файла в полето.",
    action_en: "Tab Lab -> Upload lab results -> drag and drop the file.",
    jumpId: null,
  },
  {
    id: "biological_age",
    icon: Activity, color: "#10B981",
    name_bg: "Биологична възраст",
    name_en: "Biological age",
    aiPowered: true,
    purpose_bg: "На колко години е тялото Ви според показателите в кръвта (различно от рождената дата). Показва дали отвътре сте по-млади или по-възрастни.",
    purpose_en: "How old your body is according to your biomarkers (different from your birth date). Shows if you're internally younger or older.",
    benefit_bg: "Тук се вижда дали начинът Ви на живот Ви води напред или назад в годините.",
    benefit_en: "Here you see if your lifestyle is moving you forward or backward in age.",
    action_bg: "Раздел „Лаборатории“ → дясната колона → вижте резултата след качените изследвания.",
    action_en: "Tab Lab -> right column -> see the result after uploading labs.",
    jumpId: null,
  },
  {
    id: "longevity",
    icon: Activity, color: "#10B981",
    name_bg: "Оценка за дълголетие",
    name_en: "Longevity Score",
    aiPowered: true,
    purpose_bg: "Оценка от 0 до 100, която отразява колко устойчиво е тялото Ви спрямо процесите на стареене.",
    purpose_en: "A 0-100 score reflecting how resilient your body is against aging processes.",
    benefit_bg: "Не е вълшебно число — показва кои навици и показатели тежат най-силно върху Вас.",
    benefit_en: "Not a magic number — it shows which habits and parameters weigh most on you.",
    action_bg: "Раздел „Лаборатории“ → дясната колона → вижте графиката със съставните части.",
    action_en: "Tab Lab -> right column -> see the components chart.",
    jumpId: null,
  },
  {
    id: "epimap",
    icon: Network, color: "#A855F7",
    name_bg: "Епигенетична карта",
    name_en: "Epigenetic map",
    aiPowered: true,
    purpose_bg: "Нагледна карта на хранителните пътища в тялото Ви — кои са активни, кои са приспани и какво им влияе.",
    purpose_en: "A visual map of nutrient pathways in your body — which are active, which are asleep, what affects them.",
    benefit_bg: "Помага да разберете ЗАЩО едни храни Ви се отразяват по-добре, а други — по-зле.",
    benefit_en: "Helps you understand WHY some foods feel better for you, others — worse.",
    action_bg: "Раздел „Епигенетична карта“ → разгледайте възлите и връзките.",
    action_en: "Tab Epigenetic map -> explore nodes and links.",
    jumpId: null,
  },
  {
    id: "genetic",
    icon: Dna, color: "#EC4899",
    name_bg: "Генетика и хранене",
    name_en: "Genetics (Nutrigenomics)",
    aiPowered: true,
    purpose_bg: "Ако сте качили генетични изследвания (23andMe, MyHeritage), виждате как различията в гените влияят на хранителните Ви нужди.",
    purpose_en: "If you've uploaded genetic tests (23andMe, MyHeritage), you see how gene variants affect your nutritional needs.",
    benefit_bg: "Не сме еднакви. Едни хора понасят повече кафе, други извличат по-малко витамин D от слънцето.",
    benefit_en: "We're not all the same. Some people absorb more caffeine, others — less vitamin D from sunlight.",
    action_bg: "Раздел „Генетика“ → качете изходния файл → вижте личните препоръки.",
    action_en: "Tab Genetics -> upload raw file -> see personalized recommendations.",
    jumpId: null,
  },
  {
    id: "imaging",
    icon: ScanLine, color: "#F59E0B",
    name_bg: "Разчитане на образни изследвания",
    name_en: "AI Imaging",
    aiPowered: true,
    purpose_bg: "Качвате мамография, рентгенова снимка или друго изображение — правим предварително разчитане в помощ на лекаря.",
    purpose_en: "Upload a mammogram, X-ray or other image - AI does a preliminary analysis to help your doctor.",
    benefit_bg: "Не замества лекаря, но посочва места, които си струва да обсъдите.",
    benefit_en: "Doesn't replace your doctor, but highlights points worth discussing.",
    action_bg: "Раздел „Образни изследвания“ → качете изображението → разчитането отнема минути.",
    action_en: "Tab AI Imaging -> upload image -> AI processes in minutes.",
    jumpId: null,
  },
  {
    id: "clinical",
    icon: Activity, color: "#8B5CF6",
    name_bg: "Клиничен анализ",
    name_en: "Clinical analysis",
    aiPowered: true,
    purpose_bg: "Задълбочен преглед: видове обмяна, покритие на пътищата в тялото, въздействия през ума и тялото и здравни сметки.",
    purpose_en: "Deep review: metabolic types, body pathway coverage, mind-body interventions and health calculators.",
    benefit_bg: "Тук виждате цялата картина: кои хранителни вещества Ви липсват и кои навици да добавите.",
    benefit_en: "Here you see the whole picture. Which nutrients you lack, which habits to add.",
    action_bg: "Раздел „Клиничен преглед“ → разгледайте дясната колона за конкретни препоръки.",
    action_en: "Tab Clinical -> explore the right column for concrete recommendations.",
    jumpId: null,
  },
  {
    id: "calculators",
    icon: Calculator, color: "#06B6D4",
    name_bg: "Здравни сметки",
    name_en: "Health calculators",
    purpose_bg: "Прости сметки: индекс на телесна маса, желано тегло, дневни калории, вода и обмяна в покой.",
    purpose_en: "Simple calculators: BMI, ideal weight, daily calories, water intake, BMR.",
    benefit_bg: "Бърза проверка на основните числа, без да търсите формули в мрежата.",
    benefit_en: "Quick check of basic numbers without searching for formulas online.",
    action_bg: "Раздел „Клиничен преглед“ → дясната колона → „Здравни сметки“.",
    action_en: "Tab Clinical -> right column -> Health calculators section.",
    jumpId: null,
  },
  {
    id: "nutramilo_ai",
    icon: MessageSquareText, color: "#EC4899",
    name_bg: "Разговор с Nutramilo",
    name_en: "Nutramilo AI chat",
    aiPowered: true,
    purpose_bg: "Тук говорите с петимата помощници наведнъж. Питате свободно и отговаря най-подходящият.",
    purpose_en: "Here you chat with all 5 AI assistants at once. Ask in free language and get an answer from the right specialist.",
    benefit_bg: "Разговорите се запазват — можете да се върнете и да продължите по-късно.",
    benefit_en: "Chat history is saved — you can come back and continue later.",
    action_bg: "Раздел „Разговор с Nutramilo“ → задайте въпроса си → вижте отговора с източниците.",
    action_en: "Tab Nutramilo AI -> ask your question -> see the answer with sources.",
    jumpId: null,
  },
];

export default function AnalysesGuide() {
  return (
    <MenuGuide
      modules={MODULES}
      storageKey="analyses_guide_seen"
      triggerLabelBg="Ръководство"
      triggerLabelEn="Guide"
      triggerColorClass="from-sky-500 to-cyan-500"
      testIdPrefix="analyses-guide"
    />
  );
}
